import React from "react"

import globalStyles from "../../../../App.module.scss";
import genkey from './images/genkey.png'
import encrypt from './images/encrypt.png'
import decrypt from './images/decrypt.png'
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter";
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism";

export default function RsaFileEncryption() {

    let data = `import argparse

from cryptography.hazmat.primitives.asymmetric import rsa, padding
from cryptography.hazmat.primitives import hashes, serialization
from cryptography.fernet import Fernet


def generate_keys(private_path="private_key.pem", public_path="public_key.pem"):
    private_key = rsa.generate_private_key(public_exponent=65537, key_size=2048)
    public_key = private_key.public_key()

    with open(private_path, "wb") as f:
        f.write(private_key.private_bytes(
            encoding=serialization.Encoding.PEM,
            format=serialization.PrivateFormat.PKCS8,
            encryption_algorithm=serialization.NoEncryption(),
        ))

    with open(public_path, "wb") as f:
        f.write(public_key.public_bytes(
            encoding=serialization.Encoding.PEM,
            format=serialization.PublicFormat.SubjectPublicKeyInfo,
        ))


def load_public_key(path):
    with open(path, "rb") as f:
        return serialization.load_pem_public_key(f.read())


def load_private_key(path):
    with open(path, "rb") as f:
        return serialization.load_pem_private_key(f.read(), password=None)


# RSA can only encrypt data smaller than its key size, so a random Fernet
# (AES) key does the actual file encryption; RSA only wraps that key.
def encrypt_file(in_path, out_path, public_key_path):
    public_key = load_public_key(public_key_path)
    session_key = Fernet.generate_key()

    with open(in_path, "rb") as f:
        plaintext = f.read()
    ciphertext = Fernet(session_key).encrypt(plaintext)

    wrapped_key = public_key.encrypt(
        session_key,
        padding.OAEP(
            mgf=padding.MGF1(algorithm=hashes.SHA256()),
            algorithm=hashes.SHA256(),
            label=None,
        ),
    )

    with open(out_path, "wb") as f:
        f.write(len(wrapped_key).to_bytes(2, "big"))
        f.write(wrapped_key)
        f.write(ciphertext)


def decrypt_file(in_path, out_path, private_key_path):
    private_key = load_private_key(private_key_path)

    with open(in_path, "rb") as f:
        key_length = int.from_bytes(f.read(2), "big")
        wrapped_key = f.read(key_length)
        ciphertext = f.read()

    session_key = private_key.decrypt(
        wrapped_key,
        padding.OAEP(
            mgf=padding.MGF1(algorithm=hashes.SHA256()),
            algorithm=hashes.SHA256(),
            label=None,
        ),
    )
    plaintext = Fernet(session_key).decrypt(ciphertext)

    with open(out_path, "wb") as f:
        f.write(plaintext)


def main():
    parser = argparse.ArgumentParser(description="Encrypt/decrypt files with an RSA key pair")
    subparsers = parser.add_subparsers(dest="command", required=True)

    subparsers.add_parser("genkey")

    encrypt_parser = subparsers.add_parser("encrypt")
    encrypt_parser.add_argument("input")
    encrypt_parser.add_argument("output")
    encrypt_parser.add_argument("--public-key", default="public_key.pem")

    decrypt_parser = subparsers.add_parser("decrypt")
    decrypt_parser.add_argument("input")
    decrypt_parser.add_argument("output")
    decrypt_parser.add_argument("--private-key", default="private_key.pem")

    args = parser.parse_args()

    if args.command == "genkey":
        generate_keys()
        print("Generated private_key.pem and public_key.pem")
    elif args.command == "encrypt":
        encrypt_file(args.input, args.output, args.public_key)
        print(f"Encrypted {args.input} -> {args.output}")
    elif args.command == "decrypt":
        decrypt_file(args.input, args.output, args.private_key)
        print(f"Decrypted {args.input} -> {args.output}")


if __name__ == "__main__":
    main()
`

    return (
        <div className={globalStyles.article}>
            <h1>RSA file encryption</h1>
            <p>Encrypts and decrypts files using an RSA key pair. RSA alone can only encrypt data smaller than its
                key size, so this uses hybrid encryption: a random Fernet (AES) key encrypts the file itself, and
                the RSA public key only wraps that small session key. The matching private key unwraps it again to
                decrypt.</p>
            <p>Requires the <code>cryptography</code> package: <code>pip install cryptography</code></p>
            <div style={{maxWidth: '800px'}}>
                <SyntaxHighlighter
                    children={data}
                    style={dracula}
                    PreTag="div"
                    language={'python'}
                />
            </div>
            <p>Step 1: generate the key pair.</p>
            <div style={{maxWidth: '561px'}}>
                <img src={genkey} alt={""}/>
            </div>

            <p>Step 2: encrypt the file with the public key. The output file is unreadable without the matching
                private key.</p>
            <div style={{maxWidth: '633px'}}>
                <img src={encrypt} alt={""}/>
            </div>

            <p>Step 3: decrypt the file with the private key to recover the original content.</p>
            <div style={{maxWidth: '642px'}}>
                <img src={decrypt} alt={""}/>
            </div>
        </div>
    )
}
